/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Text, TextMetrics, TextStyle } from 'pixi.js-legacy';
import { FancyDefaultStyles } from './FancyDefaultStyles';
import { FancyTextStyle } from '@appCanvasCommon/interfaces/fancy-text-style.interface';
import { getMostReadableColor } from 'utils/color-utils';
import { trimText } from '@appCanvasCommon/utils/trim-text';

/**
 * Render text at a resolution independent of the main renderer resolution
 */
export class HighResolutionText extends Text {
	public active = false;
	//public components: ComponentManager;
	public isFancy = false;
	public override dirty = false;
	public debugMode = false;
	/**
	 * This controls if we want this element to skip the text trimming step
	 * IF true, will trim input text to roughly fit the provided draw area
	 * 	- It uses the `maskArea` to determine the width and height of the rectangle
	 * 	- If there is no `maskArea` set, it will use the `wordWrapWidth` to determine the width and the `height` to determine the height
	 * 	- IF the text object has neither of these, text will not render (So set this flag to false on your text object)
	 * 	* IF false, ill not trim the input text.
	 */
	public enableTrimText: boolean = true;
	/**
	 * Stores the last resolution for this object>
	 */
	private _lastResolution: number;
	/**
	 * @param {string} text The text to render
	 * @param style {PIXI.TextStyle} The styling of the rendered text
	 * @param {HTMLCanvasElement} [canvas] The canvas to render the text to. If not provided, will create a cache canvas and render the text on that.
	 */
	constructor(
		text: string,
		style: FancyTextStyle = FancyDefaultStyles.DEFAULT_STYLE,
		canvas?: HTMLCanvasElement
	) {
		super(text, style, canvas);
		this.resolution = style.resolution || window.devicePixelRatio || 1;
		this._lastResolution = style.resolution || window.devicePixelRatio || 1;
		this.debugMode = style['debugMode'] || false;
	}

	// Intentionally stubbed out
	public calculateText(force: boolean = false) {}

	public override destroy(options?: any) {
		if (this._destroyed) {
			return;
		}
		super.destroy(options);
	}
	/**
	 * Given a string, will trim the string until it fits cleanly into a rectangle.
	 * @param {string} str The string to test against
	 * @param {number} maxWidth The max width of word wrap
	 * @param {number} maxHeight The max height of the wordWrap
	 * @param {number} heightOffset The offset from the top of the rectangle
	 */
	public autoFitString(str: string, maxWidth: number, maxHeight: number) {
		return this.autoFitStringCheck(str, str.length, maxWidth, maxHeight);
	}

	private autoFitStringCheck(
		str: string,
		length: number,
		maxWidth: number,
		maxHeight: number,
		lastLength = 0
	) {
		const slice = str.slice(0, length);
		const textDimensions = TextMetrics.measureText(slice, this.style as TextStyle, this.style['wordWrap']);
		const lines = textDimensions.width / maxWidth;
		const height = textDimensions.height * lines;

		const diff = Math.abs(lastLength - length) / 2;

		if (length === lastLength) {
			return slice;
		} else if (height > maxHeight) {
			const newLength = Math.floor(length - diff);
			if (newLength === length) {
				return slice;
			}
			return this.autoFitStringCheck(str, newLength, maxWidth, maxHeight, length);
		} else if (height <= maxHeight && lastLength !== 0) {
			const newLength = Math.floor(length + diff);
			if (newLength === length) {
				return slice;
			}
			return this.autoFitStringCheck(str, Math.floor(length + diff), maxWidth, maxHeight, lastLength);
		} else {
			return slice;
		}
	}
	/**
	 * Returns the most readable color. Uses some arbitrary values Chris came up with to blend the return color with the background.
	 * @param {string} bgColor The background color where the new text will be drawn on
	 * @returns {string}
	 */
	public getMostReadableColor(bgColor: string): string {
		return getMostReadableColor(bgColor);
	}
}
